import * as React from "react";
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useMatches,
  useRouteError,
} from "@remix-run/react";
import { json } from "@remix-run/node";
import { load as loadFathom, type LoadOptions as FathomLoadOptions } from "fathom-client";
import type { LoaderFunctionArgs } from "@remix-run/node";
import "~/styles/tailwind.css";
import "~/styles/bailwind.css";
import "~/styles/index.css";
import { removeTrailingSlashes, isProductionHost } from "./lib/http.server";
import cx from "clsx";
import { GlobalLoading } from "./components/global-loading";
import { canUseDOM } from "./components/utils";

export async function loader({ request }: LoaderFunctionArgs) {
  removeTrailingSlashes(request);

  let isDevHost = !isProductionHost(request);
  let url = new URL(request.url);

  return json(
    {
      host: url.host,
      isProductionHost: !isDevHost,
      noIndex:
        isDevHost ||
        url.pathname === "/docs/en/v1/api/remix" ||
        url.pathname === "/docs/en/v1/api/conventions",
    },
    {
      headers: {
        Vary: "Cookie",
      },
    },
  );
}

export function links() {
  let preloadedFonts = [
    "inter-roman-latin-var.woff2",
    "inter-italic-latin-var.woff2",
    "source-code-pro-roman-var.woff2",
    "source-code-pro-italic-var.woff2",
  ];
  return [
    { rel: "icon", href: "/favicon-32.png", sizes: "32x32" },
    { rel: "icon", href: "/favicon-128.png", sizes: "128x128" },
    { rel: "icon", href: "/favicon-180.png", sizes: "180x180" },
    { rel: "icon", href: "/favicon-192.png", sizes: "192x192" },
    { rel: "apple-touch-icon", href: "/favicon-180.png", sizes: "180x180" },
    ...preloadedFonts.map((font) => ({
      rel: "preload",
      as: "font",
      href: `/font/${font}`,
      crossOrigin: "anonymous",
    })),
    { rel: "alternate", type: "application/rss+xml", href: "/blog/rss.xml" },
  ];
}

interface DocumentProps {
  title?: string;
  darkBg?: string;
  isDev?: boolean;
  noIndex: boolean;
  children: React.ReactNode;
}

function Document({
  children,
  title,
  darkBg,
  noIndex,
  isDev,
}: DocumentProps) {
  let matches = useMatches();
  let isDocsPage = !!matches.find((match) => match.id.startsWith("routes/docs"));

  return (
    <html
      lang="en"
      className={cx({
        dark: true, // Force dark mode
        "scroll-pt-[6rem] lg:scroll-pt-[4rem]": isDocsPage,
      })}
    >
      <head>
        <meta charSet="utf-8" />
        <meta name="theme-color" content="#121212" />
        <meta
          name="viewport"
          content="width=device-width,initial-scale=1,viewport-fit=cover"
        />
        {noIndex && <meta name="robots" content="noindex" />}
        <Links />
        <Meta />
        {title && <title data-title-override="">{title}</title>}
      </head>

      <body
        className={cx(
          "flex min-h-screen w-full flex-col overflow-x-hidden antialiased selection:bg-blue-200 selection:text-black dark:bg-gray-900 dark:text-gray-200",
          darkBg ? darkBg : "bg-gray-900 text-gray-200"
        )}
      >
        <GlobalLoading />
          {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}export default function App() {
  let { noIndex } = useLoaderData<typeof loader>();

  if (process.env.NODE_ENV !== "development") {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useFathomClient("IRVDGCHK", {
      url: "https://cdn.usefathom.com/script.js",
      spa: "history",
      excludedDomains: ["localhost"],
    });
  }

  return (
    <Document
      noIndex={noIndex}
      isDev={process.env.NODE_ENV === "development"}
    >
      {/* The header and footer from Marketing component will now wrap the content */}
      <Outlet />
    </Document>
  );
}

export function ErrorBoundary() {
  let error = useRouteError();
  if (!canUseDOM) {
    console.error(error);
  }

  if (isRouteErrorResponse(error)) {
    return (
      <Document
        noIndex
        title={error.statusText}
        darkBg="bg-blue-brand"
      >
        <div className="flex flex-1 flex-col justify-center text-white">
          <div className="text-center leading-none">
            <h1 className="font-mono text-[25vw]">{error.status}</h1>
            <a
              className="inline-block text-[8vw] underline"
              href={`https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/${error.status}`}
            >
              {error.statusText}
            </a>
          </div>
        </div>
      </Document>
    );
  }

  return (
    <Document noIndex title="Error" darkBg="bg-red-brand">
      <div className="flex flex-1 flex-col justify-center text-white">
        <div className="text-center leading-none">
          <h1 className="text-[25vw]">Error</h1>
          <div className="text-3xl">
            Something went wrong! Please try again later.
          </div>
        </div>
      </div>
    </Document>
  );
}

function useFathomClient(siteId: string, loadOptions: FathomLoadOptions) {
  let loaded = React.useRef(false);
  React.useEffect(() => {
    if (loaded.current) return;
    loadFathom(siteId, loadOptions);
    loaded.current = true;
  }, [loadOptions, siteId]);
}
